/**
 * The rules behind the numbers: ICAO, the FAA, and GACA.
 *
 * Everything above this file reads the platform and says what the data shows.
 * That is half an answer. An operator looking at a friction survey does not
 * only want the mean — they want to know which level it has crossed, whose
 * table that level comes from, and what the standard expects to happen next.
 *
 * **How this is written, and why it matters.** Each entry names the document
 * and the part of it. Sky quotes a reference; she is not the authority, and the
 * difference is not a formality — revisions change, states file differences
 * against ICAO Annexes, and an aerodrome's own certification basis is what
 * actually binds it. So every citation carries the expectation that the
 * operator opens the current revision, and `CURRENCY` below says so in the
 * words she uses.
 *
 * **What is deliberately absent.** No threshold is invented here, and nothing
 * is cited that this file cannot name a document for. Where a number is quoted
 * — the friction levels, the lighting limits — it is quoted because it appears
 * in a published table, and the table is named. A confident wrong figure in
 * this file would be worse than an empty one: it would be wrong in the register
 * of a regulation, which is the register operators trust most.
 *
 * **The one modern nuance worth carrying.** Since the Global Reporting Format
 * took effect in November 2021, friction *measurement* is no longer the basis
 * for reporting a contaminated runway to crews — that is the runway condition
 * code, assessed from contaminant type and depth. Friction devices remain the
 * tool for **maintenance** assessment of the surface. Operators who learned the
 * older practice frequently conflate the two, and the consequences run in both
 * directions.
 */

export type Authority = "ICAO" | "FAA" | "GACA" | "ASTM";

export interface Standard {
  id: string;
  authority: Authority;
  /** The document, precisely enough to find it. */
  document: string;
  /** Where in it, when the part is worth naming. */
  part?: string;
  /** What it requires or recommends, in a sentence. */
  says: string;
  /** What that means for the screen in front of the operator. */
  implication: string;
  url?: string;
}

/** Said with every citation, because a reference without a date is a rumour. */
export const CURRENCY =
  "Citations are to the documents as I know them; revisions change and states file differences against ICAO Annexes. Your certification basis is what binds you — open the current revision before acting on any of this.";

/* ── friction and rubber ──────────────────────────────────────────────── */

export const STANDARDS: readonly Standard[] = [
  {
    id: "icao-friction-levels",
    authority: "ICAO",
    document: "Annex 14 Volume I — Aerodrome Design and Operations",
    part: "Attachment A, with ICAO Doc 9137 Airport Services Manual Part 2 (Pavement Surface Conditions)",
    says: "States establish three friction levels for each measuring device and test speed: the design objective for a new surface, a maintenance planning level, and a minimum friction level below which the surface is reported as slippery when wet and corrective action is taken.",
    implication:
      "Three levels, three different consequences. Above planning: nothing owed. Below planning: schedule work. Below minimum: corrective action and a reporting obligation — it stops being a maintenance question.",
  },
  {
    id: "icao-sft-table",
    authority: "ICAO",
    document: "ICAO Doc 9137 Part 2 / Annex 14 Vol I Attachment A friction table",
    part: "Surface Friction Tester, self-wetting, 65 km/h",
    says: "For an SFT at 65 km/h the published levels are 0.82 for a new surface, 0.60 as the maintenance planning level and 0.50 as the minimum friction level. GACAR Part 139 adopts the same table.",
    implication:
      "These are the three numbers on the friction screen. They are device-and-speed specific: the same runway measured by a different device at a different speed is judged against different figures, and comparing across devices is a common and serious error.",
  },
  {
    id: "icao-grf",
    authority: "ICAO",
    document: "Annex 14 Vol I and PANS-Aerodromes (Doc 9981) — Global Reporting Format",
    part: "In effect since November 2021",
    says: "Runway surface condition is reported to crews as a runway condition code assessed from contaminant type, depth and coverage — not from a friction measurement.",
    implication:
      "Friction devices are for maintenance assessment of the surface, not for telling a crew how slippery a contaminated runway is. Using a friction reading to report a contaminated runway is the error GRF was introduced to end; using one to plan rubber removal is exactly what it is still for.",
  },
  {
    id: "icao-rubber",
    authority: "ICAO",
    document: "ICAO Doc 9137 Part 2 (Pavement Surface Conditions)",
    part: "Maintenance of the runway surface",
    says: "Rubber deposits in the touchdown zone reduce surface friction and are to be removed when friction falls to the maintenance planning level, before the minimum is reached.",
    implication:
      "The planning level is the trigger, not the minimum. Waiting for the minimum means acting after the runway has become a reporting matter rather than before.",
  },
  {
    id: "faa-skid",
    authority: "FAA",
    document:
      "FAA AC 150/5320-12, Measurement, Construction and Maintenance of Skid-Resistant Airport Pavement Surfaces",
    says: "Recommends periodic friction surveys at an interval set by the number of daily turbojet landings per runway end, and rubber removal driven by survey results rather than by the calendar alone.",
    implication:
      "Survey frequency follows traffic, not the diary: a runway end taking more landings earns more surveys. It also means a deposition model is a planning aid, and the survey is the evidence.",
    url: "https://www.faa.gov/airports/resources/advisory_circulars",
  },
  {
    id: "faa-pavement-maint",
    authority: "FAA",
    document: "FAA AC 150/5380-6, Guidelines and Procedures for Maintenance of Airport Pavements",
    says: "Sets out preventive maintenance practice for airport pavements, including crack sealing, joint maintenance and surface treatments, and the record-keeping that supports them.",
    implication:
      "A friction loss that is not rubber is usually a pavement condition — polishing, bleeding or binder failure — and removal will not fix any of them. That is why confirming the cause comes before booking a closure.",
  },
  {
    id: "astm-pci",
    authority: "ASTM",
    document: "ASTM D5340, Standard Test Method for Airport Pavement Condition Index Surveys",
    says: "Defines the Pavement Condition Index from a sampled distress survey, giving a repeatable 0–100 condition figure.",
    implication:
      "PCI is a condition index, not a friction measure. A pavement can score well on PCI and still be polished smooth; the two answer different questions and neither substitutes for the other.",
  },

  /* ── lighting, markings, FOD ───────────────────────────────────────── */
  {
    id: "icao-agl-serviceability",
    authority: "ICAO",
    document: "Annex 14 Volume I",
    part: "Chapter 10, Aerodrome Maintenance",
    says: "A light is unserviceable when its mean intensity falls below the specified fraction of the design value, and limits are placed on the number and adjacency of unserviceable lights — tighter for precision approach operations, where adjacent failures in the same system are not permitted.",
    implication:
      "Two consecutive unserviceable lights is not twice one failure. Adjacency is what the standard restricts, because the pattern is what a pilot reads — so an alert naming consecutive units is a different severity from two scattered ones.",
  },
  {
    id: "faa-fod",
    authority: "FAA",
    document: "FAA AC 150/5210-24, Airport Foreign Object Debris (FOD) Management",
    says: "Describes FOD management programmes: inspection, removal, prevention and the recording of what was found and where.",
    implication:
      "A FOD finding is data as well as a hazard. Repeat finds in one location point at a source — a failing joint, a construction route, a vehicle — and clearing them without recording the location loses the only evidence of it.",
    url: "https://www.faa.gov/documentLibrary/media/Advisory_Circular/AC_150_5210-24.pdf",
  },

  /* ── inspection, safety management, certification ──────────────────── */
  {
    id: "faa-self-inspection",
    authority: "FAA",
    document: "14 CFR Part 139 §139.327 and FAA AC 150/5200-18, Airport Safety Self-Inspection",
    says: "A certificated airport inspects daily, and additionally after unusual conditions or events, and keeps the records for at least 12 consecutive calendar months.",
    implication:
      "A missed sheet is not a finding of nothing wrong — it is an absence of evidence, and the record is part of the requirement rather than a by-product of it.",
  },
  {
    id: "icao-annex19",
    authority: "ICAO",
    document: "Annex 19 — Safety Management, with Doc 9859 Safety Management Manual",
    says: "An SMS has four components — safety policy and objectives, safety risk management, safety assurance, safety promotion — and requires hazards to be identified and risk assessed and controlled, with the effectiveness of controls monitored.",
    implication:
      "Safety assurance is the component that fails quietly: controls that were effective when written are assumed effective forever unless something measures them. A drift check or a repeat-finding count is that measurement.",
  },
  {
    id: "gacar-139",
    authority: "GACA",
    document: "GACAR Part 139 — Certification, Authorization and Operation of Aerodromes",
    says: "The Saudi certification basis for aerodromes, including the aerodrome manual, self-inspection, pavement and lighting maintenance, and the friction levels adopted from the ICAO table.",
    implication:
      "This is the document that actually binds an aerodrome in the Kingdom. Where it differs from ICAO guidance, it governs — so a practice that is fine under an FAA AC is not thereby fine here.",
    url: "https://www.gaca.gov.sa/",
  },
  {
    id: "icao-aerodrome-manual",
    authority: "ICAO",
    document: "Annex 14 Volume I, Chapter 1 — Aerodrome Manual and certification",
    says: "The aerodrome manual records the facilities, services, equipment, operating procedures, organisation and management, and is kept current as a condition of certification.",
    implication:
      "A document whose review date has passed is a certification exposure, not an administrative one. It is also the cheapest of all findings to clear — somebody has to read it and sign.",
  },
];

/* ── finding the right ones ───────────────────────────────────────────── */

/**
 * Which standards bear on a screen.
 *
 * Mapped explicitly rather than by keyword. A regulatory citation attached to
 * the wrong screen is worse than none: it reads as authority, and an operator
 * has no way to tell a well-matched reference from a badly-matched one without
 * opening the document — which is exactly the work the citation was supposed to
 * save them.
 */
const BY_SECTION: Record<string, readonly string[]> = {
  friction: ["icao-friction-levels", "icao-sft-table", "icao-grf", "faa-skid", "faa-pavement-maint"],
  rubber: ["icao-rubber", "faa-skid", "icao-sft-table", "faa-pavement-maint"],
  "work-orders": ["gacar-139", "icao-annex19"],
  alerts: ["icao-agl-serviceability", "icao-annex19", "faa-fod"],
  inspections: ["faa-self-inspection", "faa-fod"],
  compliance: ["gacar-139", "icao-aerodrome-manual", "icao-annex19"],
  library: ["icao-aerodrome-manual", "gacar-139"],
  dashboard: ["icao-annex19"],
  projects: ["icao-annex19"],
  drift: ["icao-annex19"],
  reliability: ["icao-annex19"],
  programme: ["gacar-139"],
  liability: ["faa-pavement-maint", "astm-pci"],
};

export function standardsFor(sectionId: string): Standard[] {
  const ids = BY_SECTION[sectionId] ?? [];
  return ids.map((id) => STANDARDS.find((s) => s.id === id)).filter((s): s is Standard => Boolean(s));
}

/**
 * One line for the brief: the authority, the document, and the first sentence
 * of what it means here.
 *
 * The first sentence only. The whole implication belongs in the report — a
 * brief that grows a paragraph of regulation stops being a brief, and the point
 * of the short answer is that it can be read without stopping work.
 */
export function headlineStandard(sectionId: string): string | null {
  const first = standardsFor(sectionId)[0];
  if (!first) return null;
  const doc = first.document.split(/[,—]/)[0].trim();
  const sentence = first.implication.split(/(?<=\.)\s/)[0];
  return `${first.authority} ${doc}: ${sentence}`;
}

/** The full citations, for the report. */
export function citationLines(sectionId: string): string[] {
  const list = standardsFor(sectionId);
  if (list.length === 0) return [];
  const out = ["", "Against the standards:"];
  for (const s of list) {
    out.push(`• ${s.authority} — ${s.document}${s.part ? ` (${s.part})` : ""}`);
    out.push(`  ${s.says}`);
    out.push(`  So: ${s.implication}`);
  }
  out.push("", CURRENCY);
  return out;
}

/** Does the question ask what the rules require, rather than what the data shows? */
export function isStandardsQuestion(q: string): boolean {
  return /\b(icao|faa|gaca|gacar|easa|annex|advisory circular|regulation|standard|standards|requirement|required|compliant|compliance with|best practice|doc\s?91\d\d|part 139)\b/i.test(
    q,
  );
}

/** The reference answer for a topic, cited. */
export function standardsAnswer(sectionId: string): { brief: string[]; full: string[] } | null {
  const list = standardsFor(sectionId);
  if (list.length === 0) return null;
  const first = list[0];
  return {
    brief: [
      `${first.authority} — ${first.document}${first.part ? ` (${first.part})` : ""}.`,
      first.says,
      `So: ${first.implication}`,
    ],
    full: ["What the rules say about this screen:", ...citationLines(sectionId).slice(1)],
  };
}
